/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { createSelector } from 'reselect';
import { AppState } from '../..';
import getBoundingAreasByLocation from '../../../geofencing';
import { selectCurrentLocation } from '../geo-service';
import { CourseModel } from './models';

// TODO: should we move this somewhere common?
const alphabeticalComparator = (a: { name: string }, b: { name: string }) => {
  if (a.name === b.name) {
    return 0;
  }

  if (a.name < b.name) {
    return -1;
  }

  return 1;
};
const ordinalComparator = (a: { ordinal: number }, b: { ordinal: number }) => {
  if (a.ordinal === b.ordinal) {
    return 0;
  }

  if (a.ordinal < b.ordinal) {
    return 1;
  }

  return -1;
};

const sortCategories = (
  a: { ordinal: number; name: string },
  b: { ordinal: number; name: string },
) => {
  const ordinalResult = ordinalComparator(a, b);

  if (ordinalResult === 0) {
    return alphabeticalComparator(a, b);
  }

  return ordinalResult;
};

/**
 * Determine if a course should be shown to the user. It uses the bounding area of the logged in member to match
 * against the within and outside bounds of each course
 */
const shouldShowCourse = (
  course: CourseModel,
  boundingAreas: ReturnType<typeof getBoundingAreasByLocation>,
) => {
  const withinBounds = course.withinBoundNames || [];
  const outsideBounds = course.outsideBoundNames || [];

  // Within at least one of the bounds names
  const isWithinOneBound =
    withinBounds.length === 0
      ? true
      : !!boundingAreas.find((name) =>
          (withinBounds as string[]).includes(name),
        );

  // Not within ANY outside bounds
  const isNotInOutsideBounds = !boundingAreas.find((name) =>
    (outsideBounds as string[]).includes(name),
  );

  return isWithinOneBound && isNotInOutsideBounds;
};

const selectCourseCategories = (state: AppState) => state.courses.categories;

export const selectCourseSearchTerm = (state: AppState) =>
  state.courses.searchTerm;

export const selectSelectedCourseCategory = createSelector(
  (state: AppState) => state.courses.selectedCategory,
  selectCourseCategories,
  (selectedCategoryId, categories) => categories[selectedCategoryId || ''],
);

export const selectAllCourses = (state: AppState) => state.courses.courses;

export const selectIsFetchingCourses = (state: AppState) =>
  state.courses.fetchingCourses;

/**
 * Takes the the search results (a list of course id's returning in the API call) and searches through the
 * courses dictionary for a matching course. This allows for the client to build up a cache of courses
 * while also preventing "jank" when a user returns to the search page after having been there
 * previously. The search results get whiped out once the user leaves the course list page
 */
export const selectCourseSearchResults = createSelector(
  selectAllCourses,
  (state: AppState) => state.courses.searchResults,
  (courses, searchResults) =>
    searchResults.reduce((acc, courseId) => {
      const foundCourse = courses[courseId];
      if (foundCourse) {
        acc.push(foundCourse);
      }
      return acc;
    }, [] as CourseModel[]),
);

export const selectViewableCourses = createSelector(
  selectCurrentLocation,
  selectCourseSearchResults,
  ({ longitude, latitude }, courses) => {
    const boundingAreas = getBoundingAreasByLocation({
      longitude,
      latitude,
    });

    return courses
      .filter((course) => shouldShowCourse(course, boundingAreas))
      .sort(alphabeticalComparator);
  },
);

export const selectAllCourseCategories = createSelector(
  selectCourseCategories,
  (categories) => Object.values(categories).sort(sortCategories),
);
